"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { useGlobalPlaybackStore } from "@/lib/global-playback-store"
import type { UserScript } from "@/lib/script-store"
import type { BooruPost } from "@/lib/booru-api"
import type { TopScript } from "@/hooks/use-post-script-counts"

const BOORU_GRID_PLAY_OPTIONS = { skipHSSP: false } as const

/**
 * Device quick-play for a booru grid card — see docs/SCRIPTS_ONLY_GRID_TASK_PLAN.md, Lane D.
 *
 * The playback engine behind useGlobalPlaybackStore is a genuine singleton (see
 * lib/global-playback-store.ts's module-level `engineInstance`) — calling play()
 * with a new script already stops whatever was previously playing before it
 * starts. That solves "one physical device, many cards" for free: this hook
 * doesn't coordinate cards against each other, it just asks "is THIS card the
 * one the singleton currently belongs to" (by comparing script ids) and reacts
 * to the answer changing, the same way the detail modal's video-sync effect
 * (booru-detail-modal.tsx) does for its one video element — just re-derived
 * per card instead of assumed to be the only instance on the page.
 *
 * Free scripts only: the /play endpoint 403s for a paid script the viewer
 * doesn't own, and the grid has no per-post ownership data to gate on without
 * an extra request per visible card. Rather than a button that sometimes
 * fails silently, paid content simply doesn't get a quick-play button — the
 * detail modal (which already handles ownership/purchase) is one click away.
 */

async function fetchPlayableScript(scriptId: string) {
  const res = await fetch(`/api/marketplace/scripts/${scriptId}/play`)
  if (!res.ok) throw new Error(`Failed to load script (${res.status})`)
  const data = await res.json()
  return data.script ?? data
}

function buildQuickPlayUserScript(topScript: TopScript, post: BooruPost, playable: any): UserScript {
  const actions = playable.actions ?? []
  const dur = actions.length > 0 ? actions[actions.length - 1].at : 0
  // Derived, not hardcoded to "video" — a script can be attached to an image
  // or an audio track just as legitimately (see the audio-scripter editor),
  // and the engine plays all three the same way.
  const contentType: UserScript["contentType"] =
    post.type === "audio" ? "audio" : post.type === "video" || post.type === "gif" ? "video" : "image"
  return {
    id: topScript.id,
    name: playable.metadata?.title || post.title || "Script",
    description: "",
    contentId: String(post.id),
    contentSite: post.site,
    contentUrl: post.fileUrl,
    contentType,
    thumbnailUrl: post.previewUrl,
    funscript: {
      version: playable.version ?? "1.0",
      inverted: false,
      range: 100,
      actions,
      metadata: { ...(playable.metadata ?? {}), duration: dur },
    },
    isPublished: true,
    tags: post.tags ?? [],
    deviceCompatibility: [],
    averageIntensity: 50,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  }
}

export function useGridQuickPlay(post: BooruPost, topScript: TopScript | undefined) {
  const setGlobalScript = useGlobalPlaybackStore((s) => s.setScript)
  const globalPlay = useGlobalPlaybackStore((s) => s.play)
  const globalPause = useGlobalPlaybackStore((s) => s.pause)
  const setActiveSource = useGlobalPlaybackStore((s) => s.setActiveSource)
  const globalCurrentScriptId = useGlobalPlaybackStore((s) => s.currentScript?.id ?? null)
  const globalIsPlaying = useGlobalPlaybackStore((s) => s.isPlaying)
  const globalIsLoading = useGlobalPlaybackStore((s) => s.isLoading)

  const [localLoading, setLocalLoading] = useState(false)
  const mediaElRef = useRef<HTMLMediaElement | null>(null)
  // Set immediately before a programmatic video.play()/pause() so the
  // listeners below know to skip — otherwise this card's own commanded
  // play/pause would loop back through onPlay/onPause and re-trigger the
  // engine, exactly the feedback loop videoSyncSuppressRef exists to avoid
  // in booru-detail-modal.tsx.
  const suppressRef = useRef({ play: false, pause: false })

  const isActive = !!topScript && globalCurrentScriptId === topScript.id
  const isPlaying = isActive && globalIsPlaying
  const isLoading = localLoading || (isActive && globalIsLoading)

  // Media ↔ engine sync, scoped to only fire while this card owns the engine
  // — with many cards each holding a media element, an unscoped listener
  // would have every off-screen card quietly feed timeupdate into whichever
  // script happens to be loaded. Only attaches when this card actually HAS
  // a media element: an image post with a script has none, and the script
  // still plays fine against the device on the engine's own clock.
  useEffect(() => {
    const video = mediaElRef.current
    if (!video || !isActive || !topScript) return
    const scriptId = topScript.id

    const onPlay = () => {
      if (suppressRef.current.play) { suppressRef.current.play = false; return }
      const { currentScript, isPlaying: playing } = useGlobalPlaybackStore.getState()
      if (currentScript?.id === scriptId && !playing) globalPlay(BOORU_GRID_PLAY_OPTIONS).catch(() => {})
    }
    const onPause = () => {
      if (suppressRef.current.pause) { suppressRef.current.pause = false; return }
      const { currentScript, isPlaying: playing } = useGlobalPlaybackStore.getState()
      if (currentScript?.id === scriptId && playing) globalPause()
    }
    const onTimeUpdate = () => {
      const { currentScript, isPlaying: playing, syncVideoTime } = useGlobalPlaybackStore.getState()
      if (currentScript?.id === scriptId && playing) syncVideoTime(video.currentTime)
    }
    const onEnded = () => {
      const { currentScript, stop } = useGlobalPlaybackStore.getState()
      if (currentScript?.id === scriptId) stop()
    }

    video.addEventListener("play", onPlay)
    video.addEventListener("pause", onPause)
    video.addEventListener("timeupdate", onTimeUpdate)
    video.addEventListener("ended", onEnded)
    return () => {
      video.removeEventListener("play", onPlay)
      video.removeEventListener("pause", onPause)
      video.removeEventListener("timeupdate", onTimeUpdate)
      video.removeEventListener("ended", onEnded)
    }
  }, [isActive, topScript, globalPlay, globalPause])

  // Keeps the media element's play/pause state in lockstep with the
  // engine's, declaratively, instead of toggle() reaching for mediaElRef
  // right after an await. The overlay media element only mounts once
  // isActive flips true (see booru-post-card.tsx), so an imperative call made right
  // after setScript()/play() isn't guaranteed to land after that mount —
  // this effect re-runs on the same dependency and React guarantees ref
  // callbacks from a just-committed render have already run by the time a
  // passive effect reading them fires, so there's no race to reason about.
  useEffect(() => {
    const video = mediaElRef.current
    if (!video || !isActive) return
    if (globalIsPlaying && video.paused) {
      suppressRef.current.play = true
      video.play().catch(() => {})
    } else if (!globalIsPlaying && !video.paused) {
      suppressRef.current.pause = true
      video.pause()
    }
  }, [isActive, globalIsPlaying])

  // Another card (or another part of the app) taking over the singleton
  // engine has to visibly stop THIS card's video too, or it keeps playing
  // silently detached from the device — reads as broken, not "unscripted".
  useEffect(() => {
    if (!isActive && mediaElRef.current && !mediaElRef.current.paused) {
      suppressRef.current.pause = true
      mediaElRef.current.pause()
    }
  }, [isActive])

  const toggle = useCallback(async () => {
    if (!topScript) return

    if (isActive) {
      if (globalIsPlaying) globalPause()
      else await globalPlay(BOORU_GRID_PLAY_OPTIONS).catch(() => {})
      // Video play/pause state itself is handled by the effect above,
      // reacting to globalIsPlaying — nothing left to do here.
      return
    }

    if (localLoading) return
    setLocalLoading(true)
    try {
      const playable = await fetchPlayableScript(topScript.id)
      const userScript = buildQuickPlayUserScript(topScript, post, playable)
      setActiveSource("booru-grid")
      setGlobalScript(userScript, { skipPrewarm: true })
      await globalPlay(BOORU_GRID_PLAY_OPTIONS)
      // Video starts via the effect above once isActive flips true and the
      // overlay <video> mounts — not here, see its comment for why.
    } catch (err) {
      console.error("[useGridQuickPlay] Failed to start playback:", err)
    } finally {
      setLocalLoading(false)
    }
  }, [topScript, isActive, globalIsPlaying, globalPlay, globalPause, setGlobalScript, setActiveSource, post, localLoading])

  const registerMedia = useCallback((el: HTMLMediaElement | null) => {
    mediaElRef.current = el
  }, [])

  return { isActive, isPlaying, isLoading, toggle, registerMedia }
}
